import type {
  AdCreativeLocalizationEntry,
  AdCreativeLocalizationJob,
  AdCreativeLocalizationManifest,
  AdCreativeLocalizationScope,
} from '@segmently/schema-models/ad-creative';

export type AdCreativeLocalizationComponent = 'text' | 'images' | 'video';

/** Mirrors the server-side hard ceiling; the server remains authoritative. */
export const AD_CREATIVE_MANUAL_WEBVTT_MAX_BYTES = 256 * 1024;

export type AdCreativeImageLocalizationMode =
  | 'reuse_when_language_neutral'
  | 'localize_visible_text'
  | 'regenerate';

export type AdCreativeVideoLocalizationTier =
  | 'captions'
  | 'voice'
  | 'on_screen_text'
  | 'cultural_visuals';

export interface AdCreativeLocalizationUnsupportedReason {
  code: string;
  message: string;
}

export interface AdCreativeAiTextLocalizationContext {
  targetMarket?: string;
  tone?: string;
  marketDirection?: string;
  creativeContext?: {
    audience?: string;
    offer?: string;
    angle?: string;
    brand?: string;
    destinationSummary?: string;
  };
  glossary?: Record<string, string>;
  claims?: string[];
  protectedTokens?: string[];
  userInstruction?: string;
}

export interface AdCreativeTextLocalizationPreflightRequest {
  projectId: string;
  itemId: string;
  component: 'text';
  mode: 'ai';
  targetLocales: string[];
  sourceLocale?: string;
  model?: string;
  scope?: AdCreativeLocalizationScope;
  context?: AdCreativeAiTextLocalizationContext;
  localeProfileId?: string;
  glossaryRef?: string;
}

export interface AdCreativeTextLocalizationStartRequest
  extends AdCreativeTextLocalizationPreflightRequest {
  preflightToken: string;
}

export interface AdCreativeImageLocalizationSlotContext {
  inspection?: {
    evidenceId: string;
  };
  targetVisibleText?: string[];
  anchors?: Array<Record<string, unknown>>;
  safeZones?: Array<Record<string, unknown>>;
  allowedClaims?: string[];
  forbiddenClaims?: string[];
  protectedBrandElements?: string[];
}

export interface AdCreativeSourceImageInspectionRequest {
  projectId: string;
  itemId: string;
  slotKeys: string[];
}

export interface AdCreativeSourceImageEvidenceView {
  evidenceId: string;
  slotKey: string;
  sourceRevision: string;
  source: { assetId?: string; sha256: string; mimeType: string; width: number; height: number; contentHash: string };
  visibleText: {
    state: 'none' | 'detected' | 'unknown';
    lines?: string[];
    language?: string;
    confidence?: number;
  };
}

export interface AdCreativeSourceImageInspectionResponse {
  success: boolean;
  data?: { itemId: string; sourceRevision: string; evidence: Record<string, AdCreativeSourceImageEvidenceView> };
  error?: string;
  code?: string;
}

export interface AdCreativeImageLocalizationPreflightRequest {
  projectId: string;
  itemId: string;
  component: 'images';
  mode: AdCreativeImageLocalizationMode;
  targetLocale: string;
  slotKeys: string[];
  model: string;
  instructions?: string;
  slotContexts?: Record<string, AdCreativeImageLocalizationSlotContext>;
}

export interface AdCreativeVideoLocalizationPreflightRequest {
  projectId: string;
  itemId: string;
  component: 'video';
  mode: 'ai';
  targetLocale: string;
  sourceLocale?: string;
  videoSlotKey: string;
  tiers: AdCreativeVideoLocalizationTier[];
  models: Partial<Record<AdCreativeVideoLocalizationTier, string>>;
  instructions?: string;
  voice?: {
    mode: 'replace_with_tts';
    approvedAudioArtifactId: string;
  };
  onScreenText?: {
    inspectionEvidenceId: string;
    sourceSha256: string;
    replacements: Array<{ regionId: string; targetText: string }>;
  };
  culturalVisuals?: {
    evidenceId: string;
  };
}

export interface AdCreativeVideoTextRegionBounds {
  /** Normalized left edge in basis points, from 0 through 9,999. */
  leftBps: number;
  /** Normalized top edge in basis points, from 0 through 9,999. */
  topBps: number;
  /** Normalized right edge in basis points, from 1 through 10,000. */
  rightBps: number;
  /** Normalized bottom edge in basis points, from 1 through 10,000. */
  bottomBps: number;
}

export interface AdCreativeVideoTextInspectionRegionInput {
  regionId: string;
  sourceText: string;
  startMs: number;
  endMs: number;
  bounds: AdCreativeVideoTextRegionBounds;
}

/**
 * Human review input only. Source media identity, frame bytes, frame hashes,
 * actor identity, and storage references are deliberately server-owned.
 */
export interface AdCreativeVideoTextInspectionReviewRequest {
  projectId: string;
  itemId: string;
  slotKey: string;
  sourceRevision: string;
  regions: AdCreativeVideoTextInspectionRegionInput[];
  confirmedExactSourcePlayback: true;
}

export interface AdCreativeVideoTextInspectionEvidenceView {
  evidenceId: string;
  contentHash: string;
  immutable: true;
  status: 'approved';
  creativeItemId: string;
  slotKey: string;
  source: {
    historicalSourceRevision: string;
    sourceComponentHash: string;
    sourceAssetId: string;
    sourceSha256: string;
    mimeType: 'video/mp4' | 'video/webm';
    width: number;
    height: number;
    durationMs: number;
  };
  proof: {
    sampleSetHash: string;
    timestampsMs: number[];
    frameCount: number;
  };
  regions: Array<AdCreativeVideoTextInspectionRegionInput & { sampleIds: string[] }>;
  review: {
    origin: 'human_review';
    confirmedExactSourcePlayback: true;
    serverResampledExactFrames: true;
  };
  createdAt: string;
}

export interface AdCreativeVideoTextInspectionReviewResponse {
  success: boolean;
  data?: {
    evidence: AdCreativeVideoTextInspectionEvidenceView;
    reused: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeVideoTextInspectionListRequest {
  projectId: string;
  itemId: string;
  slotKey: string;
  sourceRevision: string;
  limit?: number;
  cursor?: string;
}

export interface AdCreativeVideoTextInspectionLibraryView {
  creativeItemId: string;
  slotKey: string;
  sourceRevision: string;
  sourceComponentHash: string;
  sourceAssetId: string;
  sourceAssetSha256: string;
  evidence: AdCreativeVideoTextInspectionEvidenceView[];
  truncated: boolean;
  nextCursor?: string;
}

export interface AdCreativeVideoTextInspectionListResponse {
  success: boolean;
  data?: { library: AdCreativeVideoTextInspectionLibraryView };
  error?: string;
  code?: string;
}

export interface AdCreativeCulturalSceneReviewInput {
  sceneId: string;
  startMs: number;
  endMs: number;
  instruction: string;
}

/**
 * Human review input only. Source/reference assets, media bytes, frame bytes,
 * hashes, actor identity, and storage locators are deliberately server-owned.
 */
export interface AdCreativeCulturalVisualReviewRequest {
  projectId: string;
  itemId: string;
  slotKey: string;
  sourceRevision: string;
  targetLocale: string;
  scenes: AdCreativeCulturalSceneReviewInput[];
  protectedReferenceSlotKeys: string[];
  confirmedExactSourcePlayback: true;
  confirmedProtectedReferences: true;
}

export interface AdCreativeCulturalVisualEvidenceView {
  evidenceId: string;
  contentHash: string;
  immutable: true;
  status: 'approved';
  creativeItemId: string;
  slotKey: string;
  targetLocale: string;
  source: AdCreativeVideoTextInspectionEvidenceView['source'];
  proof: AdCreativeVideoTextInspectionEvidenceView['proof'];
  scenes: Array<AdCreativeCulturalSceneReviewInput & { sampleIds: string[] }>;
  protectedReferences: Array<{
    referenceId: string;
    slotKey: string;
    assetId: string;
    sha256: string;
    mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
    width: number;
    height: number;
  }>;
  review: {
    origin: 'human_review';
    confirmedExactSourcePlayback: true;
    confirmedProtectedReferences: true;
    serverResampledExactFrames: true;
  };
  createdAt: string;
}

export interface AdCreativeCulturalVisualReviewResponse {
  success: boolean;
  data?: {
    evidence: AdCreativeCulturalVisualEvidenceView;
    reused: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeCulturalVisualListRequest {
  projectId: string;
  itemId: string;
  slotKey: string;
  sourceRevision: string;
  targetLocale: string;
  limit?: number;
  cursor?: string;
}

export interface AdCreativeCulturalVisualLibraryView {
  creativeItemId: string;
  slotKey: string;
  sourceRevision: string;
  sourceComponentHash: string;
  sourceAssetId: string;
  sourceAssetSha256: string;
  targetLocale: string;
  evidence: AdCreativeCulturalVisualEvidenceView[];
  truncated: boolean;
  nextCursor?: string;
}

export interface AdCreativeCulturalVisualListResponse {
  success: boolean;
  data?: { library: AdCreativeCulturalVisualLibraryView };
  error?: string;
  code?: string;
}

export type AdCreativeLocalizationPreflightRequest =
  | AdCreativeTextLocalizationPreflightRequest
  | AdCreativeImageLocalizationPreflightRequest
  | AdCreativeVideoLocalizationPreflightRequest;

export interface AdCreativeTextLocalizationInputEntry {
  entryId: string;
  kind: AdCreativeLocalizationEntry['kind'];
  sourceValue: string;
  constraints: Record<string, unknown>;
  protectedTokens: string[];
}

export interface AdCreativeTextLocalizationCostEstimate {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedUsd: number;
}

export interface AdCreativeTextLocalizationPreflight {
  component: 'text';
  preflightToken: string;
  issuedAt: string;
  expiresAt: string;
  idempotencyKey: string;
  projectId: string;
  creativeItemId: string;
  sourceRevision: string;
  sourceLocale: string;
  targetLocales: string[];
  scope: AdCreativeLocalizationScope;
  sourceManifest: AdCreativeLocalizationManifest;
  textEntries: AdCreativeTextLocalizationInputEntry[];
  context: AdCreativeAiTextLocalizationContext;
  contextHash: string;
  instructionsHash: string;
  model: string;
  provider: string;
  modelCapability: {
    id: string;
    displayName: string;
    maxInputTokens: number;
    maxOutputTokens: number;
    supportsStructuredOutput: boolean;
  };
  keySource: string;
  sourceChars: number;
  costEstimate: AdCreativeTextLocalizationCostEstimate;
}

export interface AdCreativeImageLocalizationSlotPreflight {
  slotKey: string;
  sourceInspectionEvidenceId: string;
  visibleText?: AdCreativeSourceImageEvidenceView['visibleText'];
  operation: 'reuse_source' | 'localize_visible_text' | 'regenerate';
  capability: {
    adapter: {
      supported: boolean;
      provider: string;
      model: string;
      estimatedCostUsd: number;
      unsupportedReasons?: AdCreativeLocalizationUnsupportedReason[];
    };
    verifier: Record<string, boolean>;
    billingGuard: boolean;
  };
  model: string;
  estimatedCostUsd: number;
  supported: boolean;
  unsupportedReasons: AdCreativeLocalizationUnsupportedReason[];
}

export interface AdCreativeImageLocalizationPreflight {
  component: 'images';
  issuedAt: string;
  expiresAt: string;
  sourceLocale: string;
  targetLocale: string;
  sourceRevision: string;
  manifestHash: string;
  request: Omit<AdCreativeImageLocalizationPreflightRequest, 'component'>;
  slots: AdCreativeImageLocalizationSlotPreflight[];
  estimatedCostUsd: number;
  supported: boolean;
  preflightHash: string;
}

export interface AdCreativeVideoLocalizationTierPreflight {
  tier: AdCreativeVideoLocalizationTier;
  model: string;
  adapter: {
    supported: boolean;
    provider: string;
    model: string;
    version: string;
    features: string[];
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    unsupportedReasons?: AdCreativeLocalizationUnsupportedReason[];
  };
  verifier: {
    supported: boolean;
    provider: string;
    model: string;
    version: string;
    features: string[];
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    unsupportedReasons?: AdCreativeLocalizationUnsupportedReason[];
  };
  costEstimate: AdCreativeVideoLocalizationTierCostEstimate;
  supported: boolean;
  unsupportedReasons: AdCreativeLocalizationUnsupportedReason[];
}

export interface AdCreativeVideoLocalizationTierCostEstimate {
  tier: AdCreativeVideoLocalizationTier;
  billingMode: 'ai_tokens' | 'cultural_composite' | 'unavailable';
  currency: string;
  estimatedAmountMinor: number;
  estimatedUsd?: number;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedGeneratedSeconds?: number;
  meterId?: string;
  provider?: string;
  model?: string;
  modelPriceKey?: string;
  inputTokenAmountMinorPer1k?: number;
  outputTokenAmountMinorPer1k?: number;
  unavailableReasonCode?: string;
  pricingVersion: string;
}

export interface AdCreativeVideoLocalizationCostEstimate {
  currency: string;
  complete: boolean;
  estimatedAmountMinor: number;
  estimatedUsd?: number;
  tiers: AdCreativeVideoLocalizationTierCostEstimate[];
  pricingVersion: string;
}

export interface AdCreativeVideoLocalizationPreflight {
  component: 'video';
  issuedAt: string;
  expiresAt: string;
  request: Omit<AdCreativeVideoLocalizationPreflightRequest, 'component' | 'mode'>;
  sourceLocale: string;
  targetLocale: string;
  sourceRevision: string;
  manifestHash: string;
  sourceEntryId: string;
  sourceAsset: Record<string, unknown>;
  culturalBilling?: {
    version: 'ad-creative-cultural-composite-billing-v1';
    projectId: string;
    billingScopeId: string;
    tierAttempt: number;
    requiredCapability?: string;
    generation: {
      meterId: string;
      provider: string;
      model: string;
      unit: 'generated_second';
      currency: string;
      unitAmountMinor: number;
      scenes: Array<{
        sceneId: string;
        requestedSeconds: 4 | 6 | 8;
        estimatedAmountMinor: number;
      }>;
      totalRequestedSeconds: number;
      estimatedAmountMinor: number;
    };
    verifier: {
      meterId: string;
      provider: string;
      model: string;
      generationModel: string;
      estimatedInputTokens: number;
      estimatedOutputTokens: number;
      estimatedAmountMinor: number;
      currency: string;
      batchId: 'all_scenes';
      sceneIds: string[];
    };
    estimatedTotalAmountMinor: number;
    currency: string;
    preflightHash: string;
  };
  costEstimate: AdCreativeVideoLocalizationCostEstimate;
  transcriptCapability: {
    supported: boolean;
    provider: string;
    version: string;
    features: string[];
    unsupportedReasons?: AdCreativeLocalizationUnsupportedReason[];
  };
  tiers: AdCreativeVideoLocalizationTierPreflight[];
  supported: boolean;
  preflightHash: string;
}

export type AdCreativeLocalizationPreflight =
  | AdCreativeTextLocalizationPreflight
  | AdCreativeImageLocalizationPreflight
  | AdCreativeVideoLocalizationPreflight;

export interface AdCreativeImageLocalizationStartRequest {
  projectId: string;
  itemId: string;
  component: 'images';
  mode: AdCreativeImageLocalizationMode;
  preflight: AdCreativeImageLocalizationPreflight;
}

export interface AdCreativeVideoLocalizationStartRequest {
  projectId: string;
  itemId: string;
  component: 'video';
  mode: 'ai';
  preflight: AdCreativeVideoLocalizationPreflight;
}

export type AdCreativeLocalizationStartRequest =
  | AdCreativeTextLocalizationStartRequest
  | AdCreativeImageLocalizationStartRequest
  | AdCreativeVideoLocalizationStartRequest;

export interface AdCreativeLocalizationLocaleJobResult {
  status: 'pending' | 'translating' | 'validated' | 'completed' | 'failed';
  attempts: number;
  errorCode?: string;
  errorMessage?: string;
  changedEntryIds?: string[];
  outputManifestHash?: string;
  usage?: { inputTokens: number; outputTokens: number };
  completedAt?: Date;
  updatedAt: Date;
}

/** Sanitized progress projection. Prompt input, source text, and provider URLs are intentionally absent. */
export interface AdCreativeTextLocalizationJobView
  extends Omit<AdCreativeLocalizationJob, 'createdBy' | 'idempotencyKey'> {
  status: 'queued' | 'running' | 'partial' | 'completed' | 'failed' | 'cancelled';
  model: string;
  provider: string;
  contextHash: string;
  instructionsHash: string;
  costEstimate: AdCreativeTextLocalizationCostEstimate;
  localeResults: Record<string, AdCreativeLocalizationLocaleJobResult>;
  attempt: number;
  usageTotals: { inputTokens: number; outputTokens: number };
  actualCostUsd: number;
  updatedAt: Date;
  completedAt?: Date | null;
}

export interface AdCreativeTextLocalizationTaskView {
  id: string;
  projectId: string;
  status: 'pending' | 'running' | 'retrying' | 'cancelling' | 'cancelled' | 'completed' | 'error';
  action: 'ad-creative-localization-text';
  taskType: 'ad-creative-localization-text';
  phases: Record<string, unknown>;
  currentPhaseKey: string;
  attempt: number;
  maxAttempts: number;
  partial?: boolean;
  error?: string | null;
  billing?: Record<string, unknown> | null;
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date | null;
}

export type AdCreativeLocalizationRuntimeStatus =
  | 'queued'
  | 'pending'
  | 'retrying'
  | 'running'
  | 'cancelling'
  | 'cancelled'
  | 'partial'
  | 'completed'
  | 'failed'
  | 'error';

export interface AdCreativeMediaLocalizationJobView {
  id: string;
  projectId: string;
  taskId: string;
  status: AdCreativeLocalizationRuntimeStatus;
  creativeItemId?: string;
  sourceRevision?: string;
  sourceLocale?: string;
  targetLocales?: string[];
  scope?: AdCreativeLocalizationScope;
  inputManifestHash?: string;
  result?: AdCreativeMediaLocalizationResult;
  error?: string;
  createdAt?: Date;
  updatedAt?: Date;
  completedAt?: Date | null;
}

export interface AdCreativeImageLocalizationResult {
  component: 'images';
  outcome: 'review_required' | 'partial' | 'failed';
  targetLocale: string;
  appliedSlotKeys: string[];
  failedSlotKeys: string[];
  totalCostUsd: number;
  slots: Record<string, {
    status: 'applied' | 'failed';
    operation: 'reuse_source' | 'localize_visible_text' | 'regenerate';
    attempt: number;
    retryable: boolean;
    error?: { code: string; message: string };
    usage?: { inputTokens?: number; outputTokens?: number };
    costUsd?: number;
  }>;
}

export interface AdCreativeVideoLocalizationResult {
  component: 'video';
  outcome: 'review_required' | 'partial' | 'failed';
  targetLocale: string;
  slotKey: string;
  appliedTiers: AdCreativeVideoLocalizationTier[];
  failedTiers: AdCreativeVideoLocalizationTier[];
  blockedTiers: AdCreativeVideoLocalizationTier[];
  tiers: Partial<Record<AdCreativeVideoLocalizationTier, {
    status: 'verified' | 'applied' | 'failed' | 'blocked';
    attempt: number;
    retryable: boolean;
    provider?: string;
    model?: string;
    mutationClassification?: 'rendition' | 'child_execution' | 'new_concept';
    usage?: { inputTokens?: number; outputTokens?: number };
    billing?: Record<string, unknown>;
    error?: { code: string; message: string };
  }>>;
}

export type AdCreativeMediaLocalizationResult =
  | AdCreativeImageLocalizationResult
  | AdCreativeVideoLocalizationResult;

export interface AdCreativeMediaLocalizationTaskView {
  id: string;
  projectId: string;
  status: string;
  action: 'ad-creative-localization-images' | 'ad-creative-localization-video';
  taskType: 'ad-creative-localization-images' | 'ad-creative-localization-video';
  phases?: Record<string, unknown>;
  currentPhaseKey?: string;
  attempt?: number;
  maxAttempts?: number;
  error?: string | null;
  billing?: Record<string, unknown> | null;
  result?: AdCreativeMediaLocalizationResult;
  createdAt?: Date;
  updatedAt?: Date;
  completedAt?: Date | null;
}

export interface AdCreativeTextLocalizationPreflightResponse {
  success: boolean;
  data?: { component: 'text'; preflight: AdCreativeTextLocalizationPreflight };
  error?: string;
  code?: string;
}

export interface AdCreativeTextLocalizationStartResponse {
  success: boolean;
  data?: {
    component: 'text';
    job: AdCreativeTextLocalizationJobView;
    task: AdCreativeTextLocalizationTaskView;
    reused: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeTextLocalizationJobResponse {
  success: boolean;
  data?: { component: 'text'; job: AdCreativeTextLocalizationJobView };
  error?: string;
  code?: string;
}

export interface AdCreativeImageLocalizationPreflightResponse {
  success: boolean;
  data?: { component: 'images'; preflight: AdCreativeImageLocalizationPreflight };
  error?: string;
  code?: string;
}

export interface AdCreativeVideoLocalizationPreflightResponse {
  success: boolean;
  data?: { component: 'video'; preflight: AdCreativeVideoLocalizationPreflight };
  error?: string;
  code?: string;
}

export interface AdCreativeImageLocalizationStartResponse {
  success: boolean;
  data?: {
    component: 'images';
    job: AdCreativeMediaLocalizationJobView;
    task: AdCreativeMediaLocalizationTaskView;
    reused: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeVideoLocalizationStartResponse {
  success: boolean;
  data?: {
    component: 'video';
    job: AdCreativeMediaLocalizationJobView;
    task: AdCreativeMediaLocalizationTaskView;
    reused: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeImageLocalizationJobResponse {
  success: boolean;
  data?: {
    component: 'images';
    job: AdCreativeMediaLocalizationJobView;
    task: AdCreativeMediaLocalizationTaskView;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeVideoLocalizationJobResponse {
  success: boolean;
  data?: {
    component: 'video';
    job: AdCreativeMediaLocalizationJobView;
    task: AdCreativeMediaLocalizationTaskView;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeCaptionReviewView {
  component: 'video';
  tier: 'captions' | 'voice' | 'on_screen_text';
  jobId: string;
  creativeItemId: string;
  sourceRevision: string;
  inputManifestHash: string;
  sourceLocale: string;
  targetLocale: string;
  slotKey: string;
  reviewStatus: 'review_required';
  sidecarAvailable: boolean;
  renderedDerivative: {
    assetId: string;
    playbackUrl: string;
    posterUrl?: string;
    posterAssetId?: string;
    mimeType: string;
    width: number;
    height: number;
    durationMs: number;
    sha256: string;
  };
  verificationEvidence: {
    evidenceId: string;
    evidenceHash: string;
    immutable: true;
    processVersion: string;
    createdAt: string;
    targetLocale: string;
    lineage: {
      creativeItemId: string;
      slotKey: string;
      sourceRevision: string;
      sourceAssetId: string;
      sourceSha256: string;
      inputManifestHash: string;
    };
    output: {
      sha256: string;
      byteSize: number;
      mimeType: 'video/mp4' | 'video/webm';
      width: number;
      height: number;
      durationMs: number;
    };
    tiers: Array<'captions' | 'voice' | 'on_screen_text' | 'cultural_visuals'>;
    checks: Array<{ id: string; passed: true; details?: string[] }>;
    captionArtifactId?: string;
    approvedAudioArtifactId?: string;
    approvedAudioArtifactVersionHash?: string;
    approvedAudioArtifactContentHash?: string;
    audioAssetId?: string;
    audioSha256?: string;
    voiceRightsEvidenceId?: string;
    voiceRightsVersionHash?: string;
    voiceRightsContentHash?: string;
    exactOutputInspection: {
      immutable: true;
      evidenceId: string;
      contentHash: string;
      provider: string;
      model: string;
      version: string;
      projectId: string;
      creativeItemId: string;
      slotKey: string;
      sourceRevision: string;
      outputSha256: string;
      targetLocale: string;
      observedLocale: string;
      frameSampleSetHash: string;
      sampledTimestampsMs: number[];
      checkedCueIds: string[];
      targetLanguage: { passed: true; confidence?: number; details?: string[] };
      pixelSafeArea: {
        passed: true;
        normalizedBounds: { left: number; top: number; right: number; bottom: number };
        details?: string[];
      };
      burnInRender: { passed: true; details?: string[] };
    } | {
      immutable: true;
      evidenceId: string;
      contentHash: string;
      provider: string;
      model: string;
      version: string;
      projectId: string;
      creativeItemId: string;
      slotKey: string;
      sourceRevision: string;
      sourceSha256: string;
      comparisonSha256: string;
      comparisonCompositionHash: string;
      comparisonTiers: Array<'captions' | 'voice'>;
      outputSha256: string;
      targetLocale: string;
      inspectionEvidenceId: string;
      inspectionEvidenceContentHash: string;
      renderRecipeHash: string;
      sourceFrameSampleSetHash: string;
      outputFrameSampleSetHash: string;
      sampledTimestampsMs: number[];
      checkedRegionIds: string[];
      frameOcr: {
        passed: true;
        minimumTextConfidence: number;
        minimumLocaleConfidence: number;
      };
      protectedObjects: {
        passed: true;
        maximumOutsideMeanAbsoluteError: number;
        maximumOutsideChangedPixelRatio: number;
        comparisonWidth: number;
      };
      visualContinuity: {
        passed: true;
        checkedFrameCount: number;
        maskHaloPx: number;
      };
    };
  };
  sourceAsset: { assetId: string; sha256: string; mimeType: string; durationMs: number };
  transcriptEvidence?: { artifactId: string; verifierEvidenceId: string; sourceSha256: string };
  artifact?: {
    id: string;
    immutable: true;
    contentHash: string;
    provider: string;
    model: string;
    vttUrl: string;
    vttSha256: string;
    promptVersionRefs: string[];
    createdAt: string;
  };
  verification: {
    passed: boolean;
    checks: Array<{ id: string; passed: boolean; details?: string[] }>;
  };
  cues?: Array<{
    id: string;
    startMs: number;
    endMs: number;
    speaker?: string;
    sourceText: string;
    translatedText: string;
  }>;
  onScreenTextEvidence?: {
    evidenceId: string;
    evidenceContentHash: string;
    sourceComponentHash: string;
    checkedRegionIds: string[];
    sampledTimestampsMs: number[];
  };
}

export interface AdCreativeCaptionReviewResponse {
  success: boolean;
  data?: { review: AdCreativeCaptionReviewView };
  error?: string;
  code?: string;
}

export type AdCreativeImageReviewOperation =
  | 'localize_visible_text'
  | 'regenerate';

export interface AdCreativeImageReviewTextBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AdCreativeImageReviewTextObservation {
  text: string;
  confidence: number;
  box?: AdCreativeImageReviewTextBox;
}

export interface AdCreativeImageReviewCheck {
  passed: boolean;
  confidence?: number;
  details?: string[];
}

export interface AdCreativeImageReviewSlotView {
  slotKey: string;
  operation: AdCreativeImageReviewOperation;
  /** Opaque, bounded reference to immutable backend evidence. */
  reviewRef: string;
  immutable: true;
  createdAt: string;
  lineage: {
    sourceAssetId: string;
    sourceSha256: string;
    provider: string;
    model: string;
  };
  output: {
    assetId: string;
    url: string;
    mimeType: string;
    width: number;
    height: number;
    sha256: string;
    byteSize: number;
  };
  verification: {
    provider: string;
    model: string;
    evaluationHash: string;
    checks: {
      ocr: AdCreativeImageReviewCheck;
      brand: AdCreativeImageReviewCheck;
      claims: AdCreativeImageReviewCheck;
      safeZones: AdCreativeImageReviewCheck;
    };
    expectedText: string[];
    observedText: AdCreativeImageReviewTextObservation[];
  };
}

export interface AdCreativeImageReviewView {
  component: 'images';
  jobId: string;
  creativeItemId: string;
  targetLocale: string;
  /** Exact image-component dependency pin; sourceRevision is audit-only. */
  sourceComponentHash: string;
  sourceRevision: string;
  inputManifestHash: string;
  reviewStatus: 'review_required' | 'ready' | 'stale' | 'failed';
  slots: AdCreativeImageReviewSlotView[];
}

export interface AdCreativeImageReviewResponse {
  success: boolean;
  data?: { review: AdCreativeImageReviewView };
  error?: string;
  code?: string;
}

export interface AdCreativeTimedTranscriptCueView {
  id: string;
  startMs: number;
  endMs: number;
  text: string;
  speaker?: string;
}

export interface AdCreativeTimedTranscriptView {
  artifactId: string;
  sourceAssetId: string;
  sourceSha256: string;
  sourceLocale: string;
  durationMs: number;
  verified: true;
  verifierEvidenceId: string;
  cues: AdCreativeTimedTranscriptCueView[];
}

export interface AdCreativeVideoTranscriptVersionView {
  evidenceId: string;
  origin: 'provider_stt' | 'manual_webvtt';
  sourceAssetId: string;
  sourceSha256: string;
  sourceLocale: string;
  sourceRevision: string;
  /** Exact video-slot dependency; sourceRevision is audit-only. */
  sourceComponentHash: string;
  slotKey: string;
  durationMs: number;
  cueCount: number;
  verified: true;
  verifierEvidenceId: string;
  provider: string;
  model: string;
  createdAt: string;
  selected: boolean;
  reviewerAttestation?: {
    textReviewed: true;
    timingReviewed: true;
    rightsConfirmed: true;
    reviewerId: string;
    attestedAt: string;
    note?: string;
  };
}

export interface AdCreativeVideoTranscriptLibraryView {
  creativeItemId: string;
  slotKey: string;
  sourceRevision: string;
  sourceComponentHash: string;
  sourceLocale: string;
  sourceAssetId: string;
  sourceSha256: string;
  durationMs: number;
  currentEvidenceId: string | null;
  versions: AdCreativeVideoTranscriptVersionView[];
  automaticTranscription: {
    supported: boolean;
    provider: string;
    model: string;
    version: string;
    features: string[];
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    unsupportedReasons?: AdCreativeLocalizationUnsupportedReason[];
  };
}

export interface AdCreativeVideoTranscriptListRequest {
  projectId: string;
  itemId: string;
  slotKey: string;
  sourceRevision: string;
  model?: string;
}

export interface AdCreativeVideoTranscriptReadRequest
  extends Omit<AdCreativeVideoTranscriptListRequest, 'model'> {
  evidenceId: string;
}

export interface AdCreativeVideoTranscriptCreateRequest
  extends Omit<AdCreativeVideoTranscriptListRequest, 'model'> {
  model: string;
}

export interface AdCreativeManualVideoTranscriptImportRequest
  extends Omit<AdCreativeVideoTranscriptListRequest, 'model'> {
  webVtt: string;
  reviewerAttestation: {
    textReviewed: boolean;
    timingReviewed: boolean;
    rightsConfirmed: boolean;
    note?: string;
  };
  selectAsCurrent: boolean;
}

export type AdCreativeVideoTranscriptSelectRequest = AdCreativeVideoTranscriptReadRequest;

export interface AdCreativeVideoTranscriptListResponse {
  success: boolean;
  data?: { library: AdCreativeVideoTranscriptLibraryView };
  error?: string;
  code?: string;
}

export interface AdCreativeVideoTranscriptReadResponse {
  success: boolean;
  data?: { version: AdCreativeVideoTranscriptVersionView; transcript: AdCreativeTimedTranscriptView };
  error?: string;
  code?: string;
}

export type AdCreativeVideoTranscriptTaskStatus =
  | 'pending'
  | 'running'
  | 'retrying'
  | 'cancelling'
  | 'completed'
  | 'error'
  | 'cancelled';

export interface AdCreativeVideoTranscriptTaskView {
  id: string;
  taskType: 'ad-creative-video-transcription';
  status: AdCreativeVideoTranscriptTaskStatus;
  phase: string;
  progress: number;
  projectId: string;
  creativeItemId: string;
  slotKey: string;
  sourceRevision: string;
  sourceComponentHash: string;
  provider: string;
  model: string;
  result?: { evidenceId: string; selected: false };
  error?: { code: string; message: string };
}

/** Automatic transcription is accepted for durable background delivery. */
export interface AdCreativeVideoTranscriptStartResponse {
  success: boolean;
  data?: {
    task: AdCreativeVideoTranscriptTaskView;
    reused: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeVideoTranscriptMutationResponse {
  success: boolean;
  data?: {
    evidenceId?: string;
    reused?: boolean;
    currentEvidenceId?: string;
    revision?: number;
    transcript: AdCreativeTimedTranscriptView;
  };
  error?: string;
  code?: string;
}

export type AdCreativeVoiceRightsBasis =
  | 'owner_recorded'
  | 'talent_consent'
  | 'licensed_voice'
  | 'synthetic_voice_license'
  | 'contractual_permission';

export type AdCreativeVoiceRightsOperation = 'replace' | 'mix' | 'dub';
export type AdCreativeVideoVoiceRightsStatus = 'active' | 'expired' | 'revoked';

export interface AdCreativeVideoVoiceRightsSummaryView {
  evidenceId: string;
  voiceId: string;
  rightsBasis: AdCreativeVoiceRightsBasis;
  allowedOperations: AdCreativeVoiceRightsOperation[];
  status: AdCreativeVideoVoiceRightsStatus;
  expiresAt?: string;
  createdAt: string;
  revokedAt?: string;
}

export interface AdCreativeVideoVoiceRightsLibraryView {
  creativeItemId: string;
  slotKey: string;
  sourceRevision: string;
  sourceLocale: string;
  targetLocale: string;
  sourceAssetId: string;
  sourceAssetSha256: string;
  truncated: boolean;
  evidence: AdCreativeVideoVoiceRightsSummaryView[];
}

export interface AdCreativeVideoVoiceRightsListRequest {
  projectId: string;
  itemId: string;
  slotKey: string;
  targetLocale: string;
}

export interface AdCreativeVideoVoiceRightsApproveRequest
  extends AdCreativeVideoVoiceRightsListRequest {
  voiceId: string;
  rightsBasis: AdCreativeVoiceRightsBasis;
  allowedOperations: AdCreativeVoiceRightsOperation[];
  expiresAt?: string;
  sourceDocumentRef: string;
  sourceDocumentSha256: string;
  statement: string;
}

export interface AdCreativeVideoVoiceRightsRevokeRequest
  extends AdCreativeVideoVoiceRightsListRequest {
  evidenceId: string;
  reason: string;
}

export interface AdCreativeVideoVoiceRightsListResponse {
  success: boolean;
  data?: { library: AdCreativeVideoVoiceRightsLibraryView };
  error?: string;
  code?: string;
}

export interface AdCreativeVideoVoiceRightsMutationResponse {
  success: boolean;
  data?: {
    action: 'approved' | 'revoked';
    reused: boolean;
    summary: AdCreativeVideoVoiceRightsSummaryView;
    library: AdCreativeVideoVoiceRightsLibraryView;
  };
  error?: string;
  code?: string;
}

export type AdCreativeLocalizationPreflightResponse =
  | AdCreativeTextLocalizationPreflightResponse
  | AdCreativeImageLocalizationPreflightResponse
  | AdCreativeVideoLocalizationPreflightResponse;

export type AdCreativeLocalizationStartResponse =
  | AdCreativeTextLocalizationStartResponse
  | AdCreativeImageLocalizationStartResponse
  | AdCreativeVideoLocalizationStartResponse;

export type AdCreativeLocalizationJobResponse =
  | AdCreativeTextLocalizationJobResponse
  | AdCreativeImageLocalizationJobResponse
  | AdCreativeVideoLocalizationJobResponse;

export interface AdCreativeLocalizationJobMutationResponse {
  success: boolean;
  data?: {
    component: AdCreativeLocalizationComponent;
    job: AdCreativeTextLocalizationJobView | AdCreativeMediaLocalizationJobView;
    task?: AdCreativeTextLocalizationTaskView | AdCreativeMediaLocalizationTaskView;
    alreadyTerminal?: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeLocalizationApiCall<TBody = undefined> {
  path: string;
  method: 'GET' | 'POST';
  body?: TBody;
  projectId: string;
}

export function buildLocalizationPreflightCall<TRequest extends AdCreativeLocalizationPreflightRequest>(
  request: TRequest,
): AdCreativeLocalizationApiCall<Omit<TRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localization-preflight`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildSourceImageInspectionCall(
  request: AdCreativeSourceImageInspectionRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeSourceImageInspectionRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localization-image-inspection`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildLocalizationStartCall<TRequest extends AdCreativeLocalizationStartRequest>(
  request: TRequest,
): AdCreativeLocalizationApiCall<Omit<TRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/localization-jobs`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildTextLocalizationPreflightCall(
  request: AdCreativeTextLocalizationPreflightRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeTextLocalizationPreflightRequest, 'itemId'>> {
  return buildLocalizationPreflightCall(request);
}

export function buildTextLocalizationStartCall(
  request: AdCreativeTextLocalizationStartRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeTextLocalizationStartRequest, 'itemId'>> {
  return buildLocalizationStartCall(request);
}

export function buildLocalizationJobReadCall(
  projectId: string,
  jobId: string,
  component: AdCreativeLocalizationComponent,
): AdCreativeLocalizationApiCall {
  const query = new URLSearchParams({ projectId, component });
  return {
    path: `/api/agent/ad-creative/localization-jobs/${encodeURIComponent(jobId)}?${query.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildCaptionReviewReadCall(
  projectId: string,
  jobId: string,
  targetLocale: string,
  slotKey: string,
): AdCreativeLocalizationApiCall {
  const query = new URLSearchParams({ projectId, targetLocale, slotKey });
  return {
    path: `/api/agent/ad-creative/localization-jobs/${encodeURIComponent(jobId)}/caption-review?${query.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildImageReviewReadCall(
  projectId: string,
  itemId: string,
  jobId: string,
  targetLocale: string,
  reviewRef?: string,
): AdCreativeLocalizationApiCall {
  const query = new URLSearchParams({ projectId, itemId, targetLocale });
  if (reviewRef) query.set('reviewRef', reviewRef);
  return {
    path: `/api/agent/ad-creative/localization-jobs/${encodeURIComponent(jobId)}/image-review?${query.toString()}`,
    method: 'GET',
    projectId,
  };
}

export function buildVideoTranscriptListCall(
  request: AdCreativeVideoTranscriptListRequest,
): AdCreativeLocalizationApiCall {
  const query = new URLSearchParams({
    projectId: request.projectId,
    slotKey: request.slotKey,
    sourceRevision: request.sourceRevision,
  });
  if (request.model) query.set('model', request.model);
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(request.itemId)}/video-transcripts?${query.toString()}`,
    method: 'GET',
    projectId: request.projectId,
  };
}

export function buildVideoTextInspectionReviewCall(
  request: AdCreativeVideoTextInspectionReviewRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeVideoTextInspectionReviewRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/video-text-inspections`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildVideoTextInspectionListCall(
  request: AdCreativeVideoTextInspectionListRequest,
): AdCreativeLocalizationApiCall {
  const query = new URLSearchParams({
    projectId: request.projectId,
    slotKey: request.slotKey,
    sourceRevision: request.sourceRevision,
  });
  if (request.limit !== undefined) query.set('limit', String(request.limit));
  if (request.cursor) query.set('cursor', request.cursor);
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(request.itemId)}/video-text-inspections?${query.toString()}`,
    method: 'GET',
    projectId: request.projectId,
  };
}

export function buildCulturalVisualReviewCall(
  request: AdCreativeCulturalVisualReviewRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeCulturalVisualReviewRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/cultural-visual-evidence`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildCulturalVisualListCall(
  request: AdCreativeCulturalVisualListRequest,
): AdCreativeLocalizationApiCall {
  const query = new URLSearchParams({
    projectId: request.projectId,
    slotKey: request.slotKey,
    sourceRevision: request.sourceRevision,
    targetLocale: request.targetLocale,
  });
  if (request.limit !== undefined) query.set('limit', String(request.limit));
  if (request.cursor) query.set('cursor', request.cursor);
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(request.itemId)}/cultural-visual-evidence?${query.toString()}`,
    method: 'GET',
    projectId: request.projectId,
  };
}

export function buildVideoTranscriptReadCall(
  request: AdCreativeVideoTranscriptReadRequest,
): AdCreativeLocalizationApiCall {
  const query = new URLSearchParams({
    projectId: request.projectId,
    slotKey: request.slotKey,
    sourceRevision: request.sourceRevision,
  });
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(request.itemId)}/video-transcripts/${encodeURIComponent(request.evidenceId)}?${query.toString()}`,
    method: 'GET',
    projectId: request.projectId,
  };
}

export function buildVideoTranscriptCreateCall(
  request: AdCreativeVideoTranscriptCreateRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeVideoTranscriptCreateRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/video-transcripts`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildManualVideoTranscriptImportCall(
  request: AdCreativeManualVideoTranscriptImportRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeManualVideoTranscriptImportRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/video-transcripts/import`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildVideoTranscriptSelectCall(
  request: AdCreativeVideoTranscriptSelectRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeVideoTranscriptSelectRequest, 'itemId' | 'evidenceId'>> {
  const { itemId, evidenceId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/video-transcripts/${encodeURIComponent(evidenceId)}/select`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildVideoVoiceRightsListCall(
  request: AdCreativeVideoVoiceRightsListRequest,
): AdCreativeLocalizationApiCall {
  const params = new URLSearchParams({
    projectId: request.projectId,
    slotKey: request.slotKey,
    targetLocale: request.targetLocale,
  });
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(request.itemId)}/video-voice-rights?${params.toString()}`,
    method: 'GET',
    projectId: request.projectId,
  };
}

export function buildVideoVoiceRightsApproveCall(
  request: AdCreativeVideoVoiceRightsApproveRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeVideoVoiceRightsApproveRequest, 'itemId'>> {
  const { itemId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/video-voice-rights`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildVideoVoiceRightsRevokeCall(
  request: AdCreativeVideoVoiceRightsRevokeRequest,
): AdCreativeLocalizationApiCall<Omit<AdCreativeVideoVoiceRightsRevokeRequest, 'itemId' | 'evidenceId'>> {
  const { itemId, evidenceId, ...body } = request;
  return {
    path: `/api/agent/ad-creative/items/${encodeURIComponent(itemId)}/video-voice-rights/${encodeURIComponent(evidenceId)}/revoke`,
    method: 'POST',
    body,
    projectId: request.projectId,
  };
}

export function buildLocalizationJobMutationCall(
  projectId: string,
  jobId: string,
  component: AdCreativeLocalizationComponent,
  action: 'cancel' | 'resume',
): AdCreativeLocalizationApiCall<{
  projectId: string;
  component: AdCreativeLocalizationComponent;
}> {
  return {
    path: `/api/agent/ad-creative/localization-jobs/${encodeURIComponent(jobId)}/${action}`,
    method: 'POST',
    body: { projectId, component },
    projectId,
  };
}
